// Where the hazard details panel sits — the same placement as the POI sheet
// (MapDataPOIDetailDialog#positionToMap), so a hazard and a POI open in the
// same spot: a bottom sheet on a phone; from 992px, a panel pinned to the
// top-right of the map (beside the planner's Actions column when it stands
// there), clamped to the part of the map that is on screen. Over a map that
// follows the hazard shown (a route warning, its pager), the phone's sheet is
// capped and the camera puts the hazard in the map left above it. Pure.

import { sheetRightEdge } from '../../apps/map_data_poi_dialog/helpers/sheetPlacement';

export const PANEL_DESKTOP_MIN_WIDTH = 992;
const GAP = 10;
const MIN_HEIGHT = 220;
/** A phone's sheet over a following map takes no more than this share of the window. */
export const PHONE_FOLLOWING_MAX_FRACTION = 0.5;

export interface RectLike {
  top: number;
  right: number;
  bottom: number;
}

export interface PanelPlacementInput {
  windowWidth: number;
  windowHeight: number;
  mapRect: RectLike | null;
  dockLeft: number | null;
  /** The map follows the hazard shown: a phone's sheet is capped so it stays in view. */
  followsMap?: boolean;
}

/** The phone sheet's height cap over a following map, in px. */
export function phoneFollowingMaxHeight(windowHeight: number): number {
  return Math.floor(windowHeight * PHONE_FOLLOWING_MAX_FRACTION);
}

/**
 * Inline style for the panel, or null to fall back to the stylesheet (phones,
 * no map). A phone's sheet over a following map only gets its height cap.
 */
export function hazardPanelStyle({ windowWidth, windowHeight, mapRect, dockLeft, followsMap = false }: PanelPlacementInput): Record<string, string> | null {
  if (!mapRect) return null;
  if (windowWidth < PANEL_DESKTOP_MIN_WIDTH) {
    return followsMap ? { maxHeight: `${phoneFollowingMaxHeight(windowHeight)}px` } : null;
  }

  const top = Math.max(mapRect.top, 0) + GAP;
  const bottom = Math.min(mapRect.bottom, windowHeight) - GAP;
  const rightEdge = sheetRightEdge(mapRect.right, dockLeft);

  return {
    top: `${top}px`,
    right: `${Math.max(windowWidth - rightEdge, 0) + GAP}px`,
    bottom: 'auto',
    left: 'auto',
    maxHeight: `${Math.max(bottom - top, MIN_HEIGHT)}px`,
  };
}

/**
 * Where the camera puts the hazard a following map shows, as Mapbox easeTo's
 * `offset` (px from the map's centre): on a phone, the middle of the map left
 * above the capped bottom sheet; [0, 0] from 992px, where the panel stands at
 * the side, or when there's no map above the sheet to aim for.
 */
export function followingFocusOffset({ windowWidth, windowHeight, mapRect }: Pick<PanelPlacementInput, 'windowWidth' | 'windowHeight' | 'mapRect'>): [number, number] {
  if (windowWidth >= PANEL_DESKTOP_MIN_WIDTH || !mapRect) return [0, 0];
  const visibleTop = Math.max(mapRect.top, 0);
  const sheetTop = windowHeight - GAP - phoneFollowingMaxHeight(windowHeight);
  const visibleBottom = Math.min(mapRect.bottom, windowHeight, sheetTop);
  if (visibleBottom <= visibleTop) return [0, 0];
  const target = (visibleTop + visibleBottom) / 2;
  const centre = (mapRect.top + mapRect.bottom) / 2;
  return [0, Math.round(target - centre)];
}
